import React, { useState } from 'react';

import { useUserContext } from '../../context/UserStore';
import { TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

// Goal/Task notification overlay with flipY animations for CreateGoalModal
const GoalNotificationOverlay = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.95)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 10000, // Higher than modal
  
  '&.fade-out': {
    animation: 'fadeOut 1s ease-out forwards',
  },
  
  '@keyframes fadeOut': {
    '0%': {
      opacity: 1,
    },
    '100%': {
      opacity: 0,
      visibility: 'hidden',
    }
  }
}));

// Goal notification message with flipY animations for CreateGoalModal
const GoalNotificationMessage = styled('h1')(({ theme }) => ({
  fontSize: '3.5rem',
  fontWeight: 'bold',
  color: 'white',
  textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
  margin: 0,
  animation: 'flipInY 1.5s ease-out, flipOutY 1.5s ease-in 2s forwards',
  
  '@keyframes flipInY': {
    '0%': {
      opacity: 0,
      transform: 'perspective(400px) rotateY(90deg)',
    },
    '40%': {
      transform: 'perspective(400px) rotateY(-20deg)',
    },
    '60%': {
      transform: 'perspective(400px) rotateY(10deg)',
    },
    '80%': {
      transform: 'perspective(400px) rotateY(-5deg)',
    },
    '100%': {
      opacity: 1,
      transform: 'perspective(400px) rotateY(0deg)',
    }
  },
  
  '@keyframes flipOutY': {
    '0%': {
      opacity: 1,
      transform: 'perspective(400px) rotateY(0deg)',
    },
    '30%': {
      transform: 'perspective(400px) rotateY(-20deg)',
    },
    '100%': {
      opacity: 0,
      transform: 'perspective(400px) rotateY(90deg)',
    }
  },
  
  // Responsive font sizes
  '@media (max-width: 768px)': {
    fontSize: '2.5rem',
  },
  '@media (max-width: 480px)': {
    fontSize: '2rem',
  }
}));

// White-Red-Black cycle modal box
const WhiteRedBlackModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  padding: '2rem',
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  backdropFilter: 'blur(10px)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  
  // White -> Red -> Black -> Red -> White gradient animation
  background: 'linear-gradient(-45deg, #ffffff, #ff4757, #000000, #ff4757, #ffffff)',
  backgroundSize: '500% 500%',
  animation: 'whiteRedBlackCycle 10s ease infinite',
  
  '@keyframes whiteRedBlackCycle': {
    '0%': {
      backgroundPosition: '0% 50%',
    },
    '25%': {
      backgroundPosition: '25% 50%',
    },
    '50%': {
      backgroundPosition: '50% 50%',
    },
    '75%': {
      backgroundPosition: '75% 50%',
    },
    '100%': {
      backgroundPosition: '100% 50%',
    }
  },
  
  // Dynamic text color based on background
  '& .MuiTypography-root': {
    fontWeight: 'bold',
    textShadow: '2px 2px 4px rgba(0,0,0,0.7), -1px -1px 2px rgba(255,255,255,0.7)',
    animation: 'textColorCycle 10s ease infinite',
  },
  
  '@keyframes textColorCycle': {
    '0%': { color: '#000000' },      // Black text on white
    '25%': { color: '#ffffff' },     // White text on red
    '50%': { color: '#ffffff' },     // White text on black
    '75%': { color: '#ffffff' },     // White text on red
    '100%': { color: '#000000' },    // Black text on white
  },
  
  // Style text fields
  '& .MuiTextField-root': {
    margin: '10px 0',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '8px',
  }
}));

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

export const CreateGoalModal = (props) => {
  const { onGoalCreated } = props;
  
  // Goal notification state
  const [showGoalNotification, setShowGoalNotification] = useState(false);
  const [goalNotificationMessage, setGoalNotificationMessage] = useState('');
  const [fadeOutGoalNotification, setFadeOutGoalNotification] = useState(false);
  
  // Show goal notification function
  const showGoalNotificationMessage = (message) => {
    setGoalNotificationMessage(message);
    setShowGoalNotification(true);
    setFadeOutGoalNotification(false);

    // Start fade out after 3 seconds (after flip animations)
    const fadeTimer = setTimeout(() => {
      setFadeOutGoalNotification(true);
    }, 3000);

    // Hide notification completely after fade out
    const hideTimer = setTimeout(() => {
      setShowGoalNotification(false);
      setFadeOutGoalNotification(false);
    }, 4000);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  };

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [notes, setNotes] = useState('');
  const [tasks, setTasks] = useState('');
  const { state: userState, action: userActions } = useUserContext();
  const handleChangeNotes = (event) => {
    setNotes(event.target.value);
  };
  const handleChangeTasks = (event) => {
    setTasks(event.target.value);
  };
  const handleSubmit = () => {
    let tasksArray = tasks.split('\n');
    tasksArray = tasksArray.map((item) => {
      return { name: item, done: false };
    });
    userActions.createGoalAsyncAction(notes, tasksArray);
    handleClose();
    
    // Show notification if callback provided
    if (onGoalCreated) {
      onGoalCreated();
    }

    // Show goal notification
    showGoalNotificationMessage('Goal created successfully!');
  };
  return (
    <>
      {/* Goal Notification Overlay */}
      {showGoalNotification && (
        <GoalNotificationOverlay className={fadeOutGoalNotification ? 'fade-out' : ''}>
          <GoalNotificationMessage>{goalNotificationMessage}</GoalNotificationMessage>
        </GoalNotificationOverlay>
      )}
      
      <div>
        <Button onClick={handleOpen} variant="contained" style={{borderRadius:'50px',backgroundColor:'red', border:'20px ridge green'}} className='button'>
          Create Goal
        </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <WhiteRedBlackModalBox>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Create Goal
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            Create a new goal.
          </Typography>
          <TextField 
            id="outlined-basic" 
            label="Notes" 
            variant="outlined" 
            value={notes} 
            onChange={handleChangeNotes}
            fullWidth
          />
          <TextField
            id="outlined-basic"
            label="Tasks"
            variant="outlined"
            value={tasks}
            onChange={handleChangeTasks}
            multiline
            rows={4}
            fullWidth
          />
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            sx={{ 
              mt: 2,
              background: 'rgba(255, 255, 255, 0.9)',
              color: 'black',
              fontWeight: 'bold',
              border: '2px solid rgba(0, 0, 0, 0.2)',
              borderRadius: '8px',
              padding: '10px 20px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 1)',
                transform: 'scale(1.05)',
              }
            }}
          >
            Submit
          </Button>
        </WhiteRedBlackModalBox>
      </Modal>

      {/* Goal notification overlay */}
      {showGoalNotification && (
        <GoalNotificationOverlay className={fadeOutGoalNotification ? 'fade-out' : ''}>
          <GoalNotificationMessage>
            {goalNotificationMessage}
          </GoalNotificationMessage>
        </GoalNotificationOverlay>
      )}
      </div>
    </>
  );
};
